import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle, Phone, ExternalLink, MapPin, Calendar, Car, Shield, ShieldAlert,
  Facebook, Twitter, Link2, Check, ChevronLeft, PawPrint, FileWarning, Loader2, Gavel, Users,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import SEO from "../components/SEO";

const BANNER_BY_STATUS = {
  "Help Identify":            { label: "HELP IDENTIFY",            bg: "bg-red-600",   border: "border-red-700",  text: "text-white",     sub: "Animal cruelty case" },
  "Identified":               { label: "IDENTIFIED",               bg: "bg-amber-500", border: "border-amber-700", text: "text-zinc-900",  sub: "Suspect identified — case ongoing" },
  "Arrested — Case Pending":  { label: "ARRESTED — CASE PENDING",  bg: "bg-amber-600", border: "border-amber-800", text: "text-white",     sub: "Suspect arrested — criminal case pending" },
  "Charged":                  { label: "CHARGED",                  bg: "bg-amber-500", border: "border-amber-700", text: "text-zinc-900",  sub: "Suspect charged — case ongoing" },
  "On Trial":                 { label: "ON TRIAL",                 bg: "bg-amber-500", border: "border-amber-700", text: "text-zinc-900",  sub: "Case on trial — original Help Identify record retained for history" },
  "Convicted":                { label: "CONVICTED",                bg: "bg-zinc-900",  border: "border-zinc-900",  text: "text-white",     sub: "Conviction confirmed — original Help Identify record retained for history" },
};

const PRESUMED_INNOCENT_STATUSES = new Set(["Arrested — Case Pending", "Charged", "On Trial"]);

function formatIncidentDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

export default function ActiveCaseDetail() {
  const { caseId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/active-cases/${caseId}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "Case not found." : "Could not load case.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const banner = BANNER_BY_STATUS[data?.status] || BANNER_BY_STATUS["Help Identify"];
  const sharePrefix =
    data?.status === "Convicted" ? "CONVICTED" :
    data?.status === "Arrested — Case Pending" ? "CASE UPDATE — Suspect arrested" :
    data?.status === "Identified" ? "CASE UPDATE — Suspect identified" :
    data?.status === "Charged" ? "CASE UPDATE — Suspect charged" :
    data?.status === "On Trial" ? "CASE UPDATE — On trial" :
    "HELP IDENTIFY";
  const sharePhone = data?.agency_phone ? ` Investigating agency: ${data.agency_name}${data.status === "Help Identify" ? ", " + data.agency_phone : ""}.` : "";
  const shareText = data
    ? `🚨 ${sharePrefix}: ${data.title} — ${data.city}, ${data.state_code || data.state}.${sharePhone} ${PRESUMED_INNOCENT_STATUSES.has(data.status) ? "An arrest or charge is not a conviction — presumption of innocence applies." : "Tips go to law enforcement, not Paws on Record."}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      toast.success("Share text + link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, "_blank", "noopener");
  const shareTwitter  = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener");
  // Nextdoor doesn't publish a universal share-intent URL. The reliable fallback
  // is the Nextdoor web composer at /news/post with ?body=... which pre-fills
  // the post text and link.
  const shareNextdoor = () => window.open(`https://nextdoor.com/news_feed/?body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, "_blank", "noopener");

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16 text-center text-sm text-zinc-500 inline-flex items-center gap-2 justify-center">
        <Loader2 size={14} className="animate-spin"/> Loading case…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16 text-center">
        <ShieldAlert size={32} className="mx-auto text-zinc-400" />
        <div className="font-display font-black text-2xl mt-3">{error || "Case not found."}</div>
        <Link to="/active-cases" className="btn-outline mt-6 inline-flex items-center gap-1 text-xs">
          <ChevronLeft size={12}/> Back to active cases
        </Link>
      </div>
    );
  }

  const label = banner.label;
  const tipHref = data.agency_phone ? `tel:${data.agency_phone.replace(/[^0-9+]/g, "")}` : (data.agency_tip_url || "#");

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10" data-testid="active-case-detail">
      <SEO
        title={`${data.title} — ${data.city}, ${data.state_code || data.state}`}
        description={`${label}: ${data.summary}`}
        path={`/active-cases/${data.id}`}
      />

      <Link to="/active-cases" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-zinc-600 hover:text-red-600">
        <ChevronLeft size={12}/> Back to active cases
      </Link>

      {/* Status banner — changes with status, but the original "Help Identify"
          case page always remains accessible for history. */}
      <div
        className={`mt-5 border-2 ${banner.border} ${banner.bg} ${banner.text} px-6 py-5 flex items-start gap-4`}
        data-testid="help-identify-banner"
      >
        {data.status === "Convicted" ? (
          <Gavel size={28} className="shrink-0 mt-1"/>
        ) : (
          <AlertTriangle size={28} className="shrink-0 mt-1" />
        )}
        <div>
          <div className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-none">
            {label}
          </div>
          <div className={`label-eyebrow mt-2 ${data.status === "Convicted" ? "text-zinc-300" : data.status === "Help Identify" ? "text-red-100" : "text-white/90"}`}>
            {banner.sub} · {data.agency_name}
          </div>
        </div>
      </div>

      {/* Case updates — chronological list rendered under the banner so visitors
          see the latest development immediately. The admin maintains this list
          in the Update Case modal. */}
      {Array.isArray(data.case_updates) && data.case_updates.length > 0 && (
        <div className="mt-6 border-l-4 border-amber-600 bg-amber-50 p-5" data-testid="active-case-updates">
          <div className="label-eyebrow text-amber-900">Case updates</div>
          <ol className="mt-3 space-y-4">
            {[...data.case_updates].reverse().map((u, i) => (
              <li key={i} className="text-sm leading-relaxed" data-testid={`active-case-update-${i}`}>
                <div className="text-[11px] uppercase tracking-widest font-bold font-mono text-amber-900">
                  {formatIncidentDate(u.date) || u.date}
                </div>
                <p className="mt-1 text-zinc-900">{u.text}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Presumption-of-innocence disclaimer — rendered for ANY accused-but-
          -not-convicted status (Arrested — Case Pending / Charged / On Trial).
          Hard rule, no toggle. */}
      {PRESUMED_INNOCENT_STATUSES.has(data.status) && (
        <div
          className="mt-5 border-l-4 border-zinc-900 bg-zinc-50 p-4 text-sm text-zinc-800 leading-relaxed"
          data-testid="presumption-of-innocence"
        >
          <strong className="block mb-1 uppercase tracking-widest text-[11px] font-bold text-zinc-900">
            Presumption of innocence
          </strong>
          An arrest or criminal charge is not a conviction. The accused is presumed innocent unless and until proven guilty in court.
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT COLUMN — photo + facts */}
        <div className="lg:col-span-7">
          {data.photo_url && (
            <figure className="border border-zinc-900">
              <img
                src={data.photo_url.startsWith("/api/") ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}${data.photo_url}` : data.photo_url}
                alt={data.title}
                className="w-full h-auto"
                loading="eager"
                referrerPolicy="no-referrer"
                data-testid="active-case-photo"
              />
              {(data.photo_caption || data.photo_attribution || data.photo_source_url) && (
                <figcaption className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 p-3 border-t border-zinc-200 space-y-1">
                  {data.photo_caption && <div>{data.photo_caption}</div>}
                  {(data.photo_attribution || data.photo_source_url) && (
                    <div className="normal-case tracking-normal font-sans text-[10px] text-zinc-500">
                      {data.photo_attribution}
                      {data.photo_source_url && (
                        <>
                          {data.photo_attribution ? " · " : ""}
                          <a
                            href={data.photo_source_url}
                            target="_blank"
                            rel="noreferrer"
                            className="underline hover:text-red-600"
                            data-testid="active-case-photo-source"
                          >
                            Official source
                          </a>
                        </>
                      )}
                    </div>
                  )}
                </figcaption>
              )}
            </figure>
          )}

          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-6 leading-tight" data-testid="active-case-title">
            {data.title}
          </h1>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Fact icon={MapPin} label="Location">
              {data.incident_location || `${data.city}, ${data.state_code || data.state}`}
            </Fact>
            <Fact icon={Calendar} label="Date / time">
              {formatIncidentDate(data.incident_date)}{data.incident_time ? ` · ${data.incident_time}` : ""}
            </Fact>
            <Fact icon={PawPrint} label="Animal">
              {data.animals_affected || "—"}
            </Fact>
            <Fact icon={Shield} label="Investigating agency">
              {data.agency_name}
            </Fact>
          </div>

          <div className="mt-6">
            <div className="label-eyebrow">What authorities say happened</div>
            <p className="mt-2 text-base leading-relaxed text-zinc-800" data-testid="active-case-summary">
              {data.summary}
            </p>
          </div>

          {(data.suspect_description || data.vehicle_description || data.license_plate) && (
            <div className="mt-6 border border-zinc-900 bg-white p-5">
              <div className="label-eyebrow">Officially released details</div>
              <div className="mt-3 space-y-3 text-sm">
                {data.suspect_description && (
                  <div data-testid="active-case-suspect-desc">
                    <div className="label-eyebrow text-zinc-500">Suspect description</div>
                    <div className="mt-1">{data.suspect_description}</div>
                  </div>
                )}
                {data.vehicle_description && (
                  <div data-testid="active-case-vehicle-desc">
                    <div className="label-eyebrow text-zinc-500 flex items-center gap-1"><Car size={10}/> Vehicle</div>
                    <div className="mt-1">{data.vehicle_description}</div>
                  </div>
                )}
                {data.license_plate && (
                  <div data-testid="active-case-plate">
                    <div className="label-eyebrow text-zinc-500">License plate</div>
                    <div className="mt-1 font-mono">{data.license_plate}</div>
                  </div>
                )}
                {data.case_number && (
                  <div>
                    <div className="label-eyebrow text-zinc-500">Case / report number</div>
                    <div className="mt-1 font-mono">{data.case_number}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN — action card */}
        <aside className="lg:col-span-5">
          <div className="border-2 border-zinc-900 bg-zinc-900 text-white p-6 sticky top-24">
            {data.status === "Convicted" ? (
              <>
                <div className="label-eyebrow text-zinc-400 flex items-center gap-2">
                  <Gavel size={12} /> Case outcome
                </div>
                <div className="font-display font-black text-2xl mt-2 leading-tight">
                  Suspect convicted.
                </div>
                <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                  This Help Identify page remains available as a public record of how the case progressed.
                  See the permanent registry for sentencing details.
                </p>
                <Link
                  to="/"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 transition-colors text-white font-black uppercase tracking-widest text-sm px-5 py-4 border-2 border-red-500"
                >
                  View permanent registry
                </Link>
              </>
            ) : (
              <>
                <div className="label-eyebrow text-red-500 flex items-center gap-2">
                  <FileWarning size={12} /> If you have information
                </div>
                <div className="font-display font-black text-2xl mt-2 leading-tight">
                  Call {data.agency_name} directly.
                </div>
                <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                  Tips go to the investigating law-enforcement agency, not Paws on Record. We do not collect, publish or forward tips.
                </p>

                {data.agency_phone && (
                  <a
                    href={tipHref}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 transition-colors text-white font-black uppercase tracking-widest text-sm px-5 py-4 border-2 border-red-500"
                    data-testid="i-have-information-btn"
                  >
                    <Phone size={16}/> I HAVE INFORMATION
                  </a>
                )}

                {data.agency_phone && (
                  <div className="mt-3 text-center font-mono text-lg" data-testid="active-case-agency-phone">
                    {data.agency_phone}
                  </div>
                )}

                {data.agency_tip_url && (
                  <a
                    href={data.agency_tip_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-red-500 hover:text-red-400 transition-colors text-white text-xs font-bold uppercase tracking-widest px-5 py-3"
                    data-testid="active-case-tip-url"
                  >
                    <ExternalLink size={12}/> Submit an online tip
                  </a>
                )}
              </>
            )}

            {data.source_url && (
              <a
                href={data.source_url}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-red-500 hover:text-red-400 transition-colors text-white text-xs font-bold uppercase tracking-widest px-5 py-3"
                data-testid="active-case-source"
              >
                <ExternalLink size={12}/> Source · agency / news coverage
              </a>
            )}

            <div className="mt-6 pt-5 border-t border-zinc-700">
              <div className="label-eyebrow text-zinc-400">Help identify — share this case</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={shareFacebook}
                  className="flex items-center justify-center gap-1 border border-zinc-700 hover:border-red-500 px-2 py-2 text-xs font-bold"
                  data-testid="active-case-share-fb"
                >
                  <Facebook size={14}/> Facebook
                </button>
                <button
                  type="button"
                  onClick={shareTwitter}
                  className="flex items-center justify-center gap-1 border border-zinc-700 hover:border-red-500 px-2 py-2 text-xs font-bold"
                  data-testid="active-case-share-x"
                >
                  <Twitter size={14}/> X / Twitter
                </button>
                <button
                  type="button"
                  onClick={shareNextdoor}
                  className="flex items-center justify-center gap-1 border border-zinc-700 hover:border-red-500 px-2 py-2 text-xs font-bold"
                  data-testid="active-case-share-nextdoor"
                >
                  <Users size={14}/> Nextdoor
                </button>
                <button
                  type="button"
                  onClick={copyLink}
                  className="flex items-center justify-center gap-1 border border-zinc-700 hover:border-red-500 px-2 py-2 text-xs font-bold"
                  data-testid="active-case-share-copy"
                >
                  {copied ? <Check size={14}/> : <Link2 size={14}/>} {copied ? "Copied" : "Copy text + link"}
                </button>
              </div>
              <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
                Pre-filled share includes the case image, location, HELP IDENTIFY wording and the official law-enforcement contact.
              </p>
            </div>

            <div className="mt-6 pt-5 border-t border-zinc-700 text-[11px] leading-relaxed text-zinc-400">
              <strong className="text-zinc-200">Do not confront or publicly identify individuals based on suspicion.</strong>{" "}
              If you have information, contact the investigating law-enforcement agency directly.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, children }) {
  return (
    <div className="border border-zinc-200 bg-white px-3 py-2">
      <div className="label-eyebrow text-zinc-500 flex items-center gap-1">
        <Icon size={10}/> {label}
      </div>
      <div className="mt-1 font-medium">{children}</div>
    </div>
  );
}
